<script setup lang="ts">
import { PDF_TEMPLATES } from '~/constants/cvTemplates'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Owner bar pinned above a public CV or business website.
 *
 * Only the owner sees it. It is deliberately dark (ink) so it reads as a tool
 * layer on top of the page, not as part of the published design, and it
 * carries the three owner actions: public/private access, the template and a
 * jump back into the editor. The layouts offset their own header by its
 * height (`top-14` / `mt-14`).
 */
interface Props {
  cvId?: string
  showTemplateSelector?: boolean
  showCvTemplateSelector?: boolean
  showPublicToggle?: boolean
  showEditButton?: boolean
  showMyCvsButton?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  cvId: undefined,
  showTemplateSelector: false,
  showCvTemplateSelector: false,
  showPublicToggle: false,
  showEditButton: false,
  showMyCvsButton: true
})

const emit = defineEmits<{
  'toggle-public': []
  'change-template': [template: string]
  'change-cv-template': [template: string]
}>()

const { t } = useI18n()
const user = useSupabaseUser()
const supabase = useSupabaseClient()

const userMenuOpen = ref(false)
const templateMenuOpen = ref(false)
const currentTemplate = ref<string>('signature')
const currentCvTemplate = ref<string>('classic')
const isPublic = ref<boolean>(false)
const isTogglingPublic = ref<boolean>(false)

const WEB_TEMPLATES = ['signature', 'sofax', 'classic', 'modern'] as const
const CV_TEMPLATES = PDF_TEMPLATES

/** The web and CV pickers never show together, so one dropdown serves both. */
const picker = computed(() => {
  if (props.showCvTemplateSelector) {
    return { label: t('sofax.ownerBar.cvTemplate'), options: CV_TEMPLATES, current: currentCvTemplate.value }
  }
  if (props.showTemplateSelector) {
    return { label: t('sofax.ownerBar.webTemplate'), options: WEB_TEMPLATES, current: currentTemplate.value }
  }
  return null
})

const userInitial = computed(() => user.value?.email?.charAt(0).toUpperCase() || '?')

const handleSignOut = async () => {
  try {
    await supabase.auth.signOut()
    userMenuOpen.value = false
    // Reload so the page falls back to its "private" state
    window.location.reload()
  } catch (e) {
    console.error('Error signing out:', e)
  }
}

/** The segmented control only toggles when the other side is picked. */
const setVisibility = (makePublic: boolean) => {
  if (isTogglingPublic.value || makePublic === isPublic.value) return
  isTogglingPublic.value = true
  emit('toggle-public')
}

const pickTemplate = (template: string) => {
  templateMenuOpen.value = false
  if (props.showCvTemplateSelector) {
    currentCvTemplate.value = template
    emit('change-cv-template', template)
  } else {
    currentTemplate.value = template
    emit('change-template', template)
  }
}

const toggleTemplateMenu = () => {
  templateMenuOpen.value = !templateMenuOpen.value
  userMenuOpen.value = false
}

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value
  templateMenuOpen.value = false
}

const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.closest('[data-owner-template-menu]')) templateMenuOpen.value = false
  if (!target.closest('[data-owner-user-menu]')) userMenuOpen.value = false
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    templateMenuOpen.value = false
    userMenuOpen.value = false
  }
}

const handlePublicChanged = (event: Event) => {
  isPublic.value = (event as CustomEvent<{ isPublic: boolean }>).detail.isPublic
  isTogglingPublic.value = false
}

const handleToggleComplete = () => {
  isTogglingPublic.value = false
}

onMounted(async () => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('public-status-changed-from-page', handlePublicChanged)
  window.addEventListener('toggle-public-status-complete', handleToggleComplete)

  if (props.cvId) {
    try {
      const { data } = await supabase
        .from('cv_requests')
        .select('is_public, business_web_template, template')
        .eq('id', props.cvId)
        .single<{ is_public: boolean | null, business_web_template: string | null, template: string | null }>()

      if (data) {
        isPublic.value = data.is_public || false
        currentTemplate.value = data.business_web_template || 'signature'
        currentCvTemplate.value = data.template || 'classic'
      }
    } catch (e) {
      console.error('Error loading CV data for admin panel:', e)
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('public-status-changed-from-page', handlePublicChanged)
  window.removeEventListener('toggle-public-status-complete', handleToggleComplete)
})

defineExpose({
  setPublicStatus: (status: boolean) => {
    isPublic.value = status
  },
  setTemplate: (template: string) => {
    currentTemplate.value = template
  },
  setCvTemplate: (template: string) => {
    currentCvTemplate.value = template
  },
  setTogglingState: (state: boolean) => {
    isTogglingPublic.value = state
  }
})

/** Shared look of the translucent pills on the dark bar. */
const ghostPill = 'inline-flex h-9 items-center gap-2 rounded-full bg-white/10 px-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac'
const menuItem = 'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors duration-300'
</script>

<template>
  <div
    class="sofax fixed inset-x-0 top-0 z-[60] border-b border-white/10 bg-ink text-white shadow-lift"
    role="region"
    :aria-label="t('sofax.ownerBar.title')"
  >
    <div class="mx-auto flex h-14 max-w-[81rem] items-center justify-between gap-2 px-3 sm:px-6">
      <!-- Left: what this bar is -->
      <div class="flex min-w-0 items-center gap-2.5">
        <div class="flex size-8 shrink-0 items-center justify-center rounded-full bg-lilac text-ink">
          <i class="bi bi-shield-lock-fill text-sm" aria-hidden="true" />
        </div>
        <div class="hidden min-w-0 leading-tight sm:block">
          <div class="truncate font-title text-sm font-bold text-white">{{ t('sofax.ownerBar.title') }}</div>
          <div class="hidden truncate text-xs text-white/60 lg:block">{{ t('sofax.ownerBar.onlyYou') }}</div>
        </div>
      </div>

      <!-- Right: owner actions -->
      <div class="flex items-center gap-1.5 sm:gap-2">
        <!-- Access: private / public -->
        <div v-if="showPublicToggle" class="flex items-center gap-3">
          <div
            class="flex items-center rounded-full bg-white/10 p-1"
            role="group"
            :aria-label="t('sofax.ownerBar.visibility')"
            :class="{ 'opacity-60': isTogglingPublic }"
          >
            <button
              type="button"
              class="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-bold transition-colors duration-300 sm:px-3"
              :class="!isPublic ? 'bg-white text-ink' : 'text-white/70 hover:text-white'"
              :aria-pressed="!isPublic"
              :disabled="isTogglingPublic"
              :title="t('sofax.ownerBar.privateHint')"
              @click="setVisibility(false)"
            >
              <i class="bi bi-lock-fill" aria-hidden="true" />
              <div class="hidden sm:block">{{ t('sofax.ownerBar.private') }}</div>
            </button>
            <button
              type="button"
              class="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-bold transition-colors duration-300 sm:px-3"
              :class="isPublic ? 'bg-accent text-white' : 'text-white/70 hover:text-white'"
              :aria-pressed="isPublic"
              :disabled="isTogglingPublic"
              :title="t('sofax.ownerBar.publicHint')"
              @click="setVisibility(true)"
            >
              <i v-if="isTogglingPublic" class="bi bi-arrow-repeat inline-block animate-spin" aria-hidden="true" />
              <i v-else class="bi bi-globe2" aria-hidden="true" />
              <div class="hidden sm:block">{{ t('sofax.ownerBar.public') }}</div>
            </button>
          </div>
          <div class="hidden text-xs text-white/60 xl:block" aria-live="polite">
            {{ isPublic ? t('sofax.ownerBar.publicHint') : t('sofax.ownerBar.privateHint') }}
          </div>
        </div>

        <!-- Template picker -->
        <div v-if="picker" class="relative" data-owner-template-menu>
          <button
            type="button"
            :class="ghostPill"
            :aria-expanded="templateMenuOpen"
            aria-haspopup="menu"
            :aria-label="picker.label"
            @click.stop="toggleTemplateMenu"
          >
            <i class="bi bi-palette" aria-hidden="true" />
            <div class="hidden md:block">
              <span class="!text-white/60">{{ t('sofax.ownerBar.design') }}:</span>
              {{ t(`sofax.ownerBar.templates.${picker.current}`) }}
            </div>
            <i
              class="bi bi-chevron-down text-xs transition-transform duration-300"
              :class="{ 'rotate-180': templateMenuOpen }"
              aria-hidden="true"
            />
          </button>

          <Transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="-translate-y-1 opacity-0"
            leave-active-class="transition duration-100 ease-in"
            leave-to-class="-translate-y-1 opacity-0"
          >
            <div
              v-if="templateMenuOpen"
              class="absolute right-0 top-full z-10 mt-3 w-56 overflow-hidden rounded-[0.625rem] border border-line bg-white shadow-lift dark:border-gray-700 dark:bg-gray-800"
              role="menu"
            >
              <div class="border-b border-line px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted dark:border-gray-700 dark:text-gray-400">
                {{ picker.label }}
              </div>
              <div class="p-2">
                <button
                  v-for="option in picker.options"
                  :key="option"
                  type="button"
                  role="menuitemradio"
                  :aria-checked="picker.current === option"
                  :class="[menuItem, picker.current === option
                    ? 'bg-surface font-semibold text-ink dark:bg-gray-700 dark:text-white'
                    : 'text-body hover:bg-surface hover:text-ink dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white']"
                  @click="pickTemplate(option)"
                >
                  <div class="flex-1">{{ t(`sofax.ownerBar.templates.${option}`) }}</div>
                  <i v-if="picker.current === option" class="bi bi-check-lg text-base text-accent" aria-hidden="true" />
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <div class="mx-1 hidden h-6 w-px bg-white/15 sm:block" />

        <LocaleLink
          v-if="showMyCvsButton"
          to="/cv"
          :class="ghostPill"
          :aria-label="t('sofax.ownerBar.myCvs')"
        >
          <i class="bi bi-files" aria-hidden="true" />
          <div class="hidden lg:block">{{ t('sofax.ownerBar.myCvs') }}</div>
        </LocaleLink>

        <LocaleLink
          v-if="showEditButton && cvId"
          :to="`/cv/${cvId}`"
          class="inline-flex h-9 items-center gap-2 rounded-full bg-accent px-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac sm:px-4"
          :aria-label="t('sofax.ownerBar.edit')"
        >
          <i class="bi bi-pencil-square" aria-hidden="true" />
          <div class="hidden md:block">{{ t('sofax.ownerBar.edit') }}</div>
        </LocaleLink>

        <!-- Account -->
        <div class="relative" data-owner-user-menu>
          <button
            type="button"
            class="flex items-center gap-1.5 rounded-full p-1 transition-colors duration-300 hover:bg-white/10"
            :aria-label="t('sofax.ownerBar.account')"
            :aria-expanded="userMenuOpen"
            aria-haspopup="menu"
            @click.stop="toggleUserMenu"
          >
            <div class="flex size-8 items-center justify-center rounded-full bg-white font-title text-sm font-bold text-ink">
              {{ userInitial }}
            </div>
            <i
              class="bi bi-chevron-down hidden text-xs text-white transition-transform duration-300 sm:inline-block"
              :class="{ 'rotate-180': userMenuOpen }"
              aria-hidden="true"
            />
          </button>

          <Transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="-translate-y-1 opacity-0"
            leave-active-class="transition duration-100 ease-in"
            leave-to-class="-translate-y-1 opacity-0"
          >
            <div
              v-if="userMenuOpen"
              class="absolute right-0 top-full z-10 mt-3 w-64 overflow-hidden rounded-[0.625rem] border border-line bg-white shadow-lift dark:border-gray-700 dark:bg-gray-800"
              role="menu"
            >
              <div class="border-b border-line px-4 py-3 dark:border-gray-700">
                <div class="truncate font-title text-base font-semibold text-ink dark:text-white">{{ user?.email }}</div>
                <div class="mt-0.5 text-xs text-muted dark:text-gray-400">{{ t('sofax.ownerBar.signedIn') }}</div>
              </div>
              <div class="p-2">
                <LocaleLink
                  to="/profile"
                  :class="[menuItem, 'text-body hover:bg-surface hover:text-ink dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white']"
                  role="menuitem"
                  @click="userMenuOpen = false"
                >
                  <i class="bi bi-person text-lg" aria-hidden="true" />
                  {{ t('sofax.ownerBar.profile') }}
                </LocaleLink>
                <button
                  type="button"
                  :class="[menuItem, 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20']"
                  role="menuitem"
                  @click="handleSignOut"
                >
                  <i class="bi bi-box-arrow-right text-lg" aria-hidden="true" />
                  {{ t('sofax.ownerBar.signOut') }}
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>
